import { CsvUpload } from '@/components/dataset/CsvUpload';
import { KnowledgeEditor } from '@/components/knowledge/KnowledgeEditor';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useDataset, useDatasetsList } from '@/hooks/useDatasets';
import { useCreateDiscovery } from '@/hooks/useDiscoveries';
import { useCreateKnowledge, useKnowledgeList } from '@/hooks/useKnowledge';
import { extractApiError } from '@/lib/api';
import type { Dataset } from '@/lib/datasets';
import { friendlyError } from '@/lib/error-messages';
import { type KnowledgePayload, deriveKinds } from '@/lib/knowledge';
import { zodResolver } from '@hookform/resolvers/zod';
import { Lightbulb, TriangleAlert } from 'lucide-react';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';
import { z } from 'zod';

// Sync-mode is a server-side hint — backend gates it on dataset shape and
// queue depth, with a 12s wait budget. PERFECTION on real-world data scales
// roughly with rows × cols² — a ~900K-cell input (e.g. SACHS at 7466×11×11)
// takes ~60s, blowing past the budget. The 408 fallback path then has to do
// DB cleanup while the still-running compute thread holds the GIL, so the
// timeout itself can take 18+ seconds to land — past axios' patience.
// Keep this aggressive: only TRUE toys (a few thousand cells) try sync.
const SYNC_CELL_BUDGET = 50_000;

// Radix Select cannot hold an empty string — map "no knowledge" → this token.
const NO_KNOWLEDGE = '__none__';

const EMPTY_KNOWLEDGE: KnowledgePayload = {
  edges_forbidden: [],
  edges_required: [],
  tiers: [],
};

function isPayloadEmpty(p: KnowledgePayload): boolean {
  return (
    p.edges_forbidden.length === 0 && p.edges_required.length === 0 && p.tiers.length === 0
  );
}

const formSchema = z.object({
  datasetId: z.string().min(1, 'Pick a dataset'),
  knowledgeId: z.string(),
  seed: z.coerce
    .number()
    .int()
    .min(0)
    .max(2 ** 31 - 1),
});

type FormValues = z.infer<typeof formSchema>;

export default function DiscoveryNew() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedDataset = searchParams.get('dataset_id') ?? undefined;
  const [mode, setMode] = React.useState<'pick' | 'upload'>('pick');

  // 100 covers the typical workspace; a larger workspace will land here
  // with truncated options + the inline "Upload new" path covers the rest.
  const datasetsQuery = useDatasetsList({ limit: 100 });

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      datasetId: preselectedDataset ?? '',
      knowledgeId: NO_KNOWLEDGE,
      seed: 42,
    },
  });

  const selectedDatasetId = form.watch('datasetId');
  // Column count of the picked dataset, read from the already-fetched list.
  // Drives an honest, non-blocking runtime/readability warning below.
  const selectedDatasetCols = datasetsQuery.data?.items.find(
    (d) => d.id === selectedDatasetId,
  )?.n_cols;
  // List org-wide KOs PLUS those pinned to the picked dataset. Backend's
  // ?dataset_id filter implements both, but only once a dataset is picked.
  // When none is picked yet we just show org-wide rows (no filter param).
  const knowledgeQuery = useKnowledgeList(
    selectedDatasetId ? { dataset_id: selectedDatasetId, limit: 100 } : { limit: 100 },
  );

  // Columns for the inline editor — populated once a dataset is picked.
  const datasetQuery = useDataset(selectedDatasetId || undefined);
  const columns = datasetQuery.data?.column_names ?? [];

  // Inline knowledge draft (only consumed when the user expands the
  // "Add domain knowledge" disclosure and edits something). Reset whenever
  // the dataset changes — variable names may differ between datasets.
  const [inlinePayload, setInlinePayload] = React.useState<KnowledgePayload>(EMPTY_KNOWLEDGE);

  // Reset knowledge picker when the dataset changes — a KO pinned to dataset
  // A would 422 against dataset B server-side (knowledge.dataset_mismatch).
  // setValue inside an effect keeps a stale id from sticking around.
  const previousDataset = React.useRef(selectedDatasetId);
  React.useEffect(() => {
    if (previousDataset.current !== selectedDatasetId) {
      previousDataset.current = selectedDatasetId;
      form.setValue('knowledgeId', NO_KNOWLEDGE, { shouldValidate: false });
      setInlinePayload(EMPTY_KNOWLEDGE);
    }
  }, [selectedDatasetId, form]);

  // Mutation that persists an inline knowledge payload as a fresh KO right
  // before kicking off the discovery. Server treats it like any saved KO —
  // the discovery will reference it via knowledge_id.
  const createKnowledge = useCreateKnowledge();

  const create = useCreateDiscovery({
    onSuccess: (job) => {
      navigate(`/discoveries/${job.id}`, { replace: true });
    },
    onError: (err) => {
      const apiErr = extractApiError(err);
      // The 408 sync-timeout still includes the job in body — but axios
      // surfaces it as an error. Read job_id from metadata and navigate.
      if (apiErr?.code === 'discovery.sync_timeout') {
        const jobId = (apiErr.metadata as { job_id?: string } | undefined)?.job_id;
        if (jobId) {
          toast.info('Switched to background mode — tracking on the details page.');
          navigate(`/discoveries/${jobId}`, { replace: true });
          return;
        }
      }
      // Quota — surface details so the user knows whether it's the period
      // or lifetime cap.
      if (apiErr?.code === 'quota.exceeded') {
        const meta = apiErr.metadata as { cap_hit?: string } | undefined;
        toast.error(`${friendlyError(apiErr.code, apiErr.message)} (${meta?.cap_hit ?? 'cap'})`);
        return;
      }
      toast.error(friendlyError(apiErr?.code, apiErr?.message ?? 'Failed to start discovery.'));
    },
  });

  function onPicked(dataset: Dataset) {
    form.setValue('datasetId', dataset.id, { shouldValidate: true });
    setMode('pick');
    datasetsQuery.refetch();
  }

  async function submit(values: FormValues) {
    const ds = datasetsQuery.data?.items.find((d) => d.id === values.datasetId);
    const cells = ds ? ds.n_rows * ds.n_cols * ds.n_cols : Number.MAX_SAFE_INTEGER;
    const preferWait = cells < SYNC_CELL_BUDGET;
    let knowledgeId = values.knowledgeId === NO_KNOWLEDGE ? null : values.knowledgeId;

    // Inline-knowledge path: if the user opened the disclosure and filled
    // anything in, persist it as a fresh KO first. We do this client-side
    // (vs. a single combined endpoint) because the backend treats KOs as
    // first-class objects — keeps fingerprinting + audit consistent.
    if (!isPayloadEmpty(inlinePayload)) {
      const allEdges = [...inlinePayload.edges_forbidden, ...inlinePayload.edges_required];
      if (allEdges.some(([a, b]) => !a || !b)) {
        toast.error('Every knowledge edge needs both a source and a target.');
        return;
      }
      if (allEdges.some(([a, b]) => a === b)) {
        toast.error('Knowledge edges cannot be self-loops.');
        return;
      }
      try {
        const ko = await createKnowledge.mutateAsync({
          // Auto-name so the user can find it later under /knowledge. They
          // can rename it from the standalone page.
          name: `Inline · ${ds?.name ?? values.datasetId} · ${new Date().toISOString().slice(0, 16).replace('T', ' ')}`,
          dataset_id: values.datasetId,
          kinds: deriveKinds(inlinePayload),
          payload: inlinePayload,
        });
        knowledgeId = ko.id;
        toast.success('Inline knowledge saved — attached to this run.');
      } catch (err) {
        const apiErr = extractApiError(err);
        toast.error(friendlyError(apiErr?.code, apiErr?.message ?? 'Failed to save knowledge.'));
        return;
      }
    }

    create.mutate({
      body: {
        dataset_id: values.datasetId,
        knowledge_id: knowledgeId,
        params: { seed: values.seed },
      },
      options: { idempotencyKey: uuidv4(), preferWait },
    });
  }

  const isPending = create.isPending || createKnowledge.isPending;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">New discovery</h1>
        <p className="text-sm text-muted-foreground">
          Pick a dataset and run PERFECTION to recover a causal graph.
        </p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(submit)} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">1. Dataset</CardTitle>
              <CardDescription>Choose an existing dataset or upload a new one.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {mode === 'pick' ? (
                <>
                  <FormField
                    control={form.control}
                    name="datasetId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Dataset</FormLabel>
                        <FormControl>
                          <Select
                            value={field.value}
                            onValueChange={field.onChange}
                            disabled={isPending}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select a dataset" />
                            </SelectTrigger>
                            <SelectContent>
                              {(datasetsQuery.data?.items ?? []).map((d) => (
                                <SelectItem key={d.id} value={d.id}>
                                  {d.name} ({d.n_rows.toLocaleString()} × {d.n_cols})
                                </SelectItem>
                              ))}
                              {datasetsQuery.data?.items.length === 0 ? (
                                <div className="px-2 py-1.5 text-sm text-muted-foreground">
                                  No datasets — upload one below.
                                </div>
                              ) : null}
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  {selectedDatasetCols !== undefined && selectedDatasetCols > 20 ? (
                    <Alert className="border-amber-500/50 bg-amber-500/5 text-amber-900 dark:border-amber-500/40 dark:text-amber-200 [&>svg]:text-amber-600">
                      <TriangleAlert className="h-4 w-4" />
                      <AlertDescription className="text-amber-900 dark:text-amber-200">
                        This dataset has {selectedDatasetCols} variables. Discovery on large
                        networks can take several minutes (longer for categorical data), and graphs
                        over ~15-20 variables are hard to read. Consider running on the variables you
                        most want to reason about.
                      </AlertDescription>
                    </Alert>
                  ) : selectedDatasetCols !== undefined && selectedDatasetCols >= 12 ? (
                    <p className="text-sm text-muted-foreground">
                      Tip: causal graphs are easiest to interpret with ~15 variables or fewer.
                    </p>
                  ) : null}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setMode('upload')}
                    disabled={isPending}
                  >
                    Upload new dataset
                  </Button>
                </>
              ) : (
                <div className="space-y-2">
                  <CsvUpload onUploaded={onPicked} />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setMode('pick')}
                    disabled={isPending}
                  >
                    Back to existing datasets
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">2. Domain knowledge (optional)</CardTitle>
              <CardDescription>
                Attach forbidden/required edges or tiers to refine the graph.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="knowledgeId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Reuse a saved knowledge object</FormLabel>
                    <FormControl>
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                        disabled={isPending}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="No knowledge" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={NO_KNOWLEDGE}>None</SelectItem>
                          {(knowledgeQuery.data?.items ?? []).map((k) => (
                            <SelectItem key={k.id} value={k.id}>
                              {k.name}
                              {k.dataset_id ? '' : ' · org-wide'}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormDescription>
                      <Link
                        to="/knowledge/new"
                        className="inline-flex items-center gap-1 underline"
                      >
                        <Lightbulb className="h-3 w-3" /> Create new knowledge
                      </Link>
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/*
                Inline knowledge editor. Collapsed by default — most runs don't
                need it, and an expanded editor with empty selects is noise.
                Disabled when a saved KO is already selected to avoid the
                "which one wins?" question — we route around it by clearing
                the dropdown in onClick of the trigger.
              */}
              <Accordion type="single" collapsible className="rounded-md border">
                <AccordionItem value="inline-knowledge" className="border-b-0">
                  <AccordionTrigger className="px-4 text-sm">
                    <span className="flex items-center gap-2">
                      <Lightbulb className="h-4 w-4 text-muted-foreground" />
                      Add domain knowledge inline
                      {!isPayloadEmpty(inlinePayload) ? (
                        <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                          {inlinePayload.edges_forbidden.length +
                            inlinePayload.edges_required.length +
                            inlinePayload.tiers.length}{' '}
                          set
                        </span>
                      ) : null}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="px-4">
                    {!selectedDatasetId ? (
                      <p className="text-sm text-muted-foreground">
                        Pick a dataset first — we need its column names to populate the variable
                        pickers.
                      </p>
                    ) : datasetQuery.isLoading ? (
                      <p className="text-sm text-muted-foreground">Loading variables…</p>
                    ) : (
                      /*
                        Tabs route the user through one concern at a time —
                        less visually overwhelming than the standalone /knowledge
                        page where all three sections are stacked. KnowledgeEditor
                        now accepts a `section` prop so each TabsContent renders
                        only the relevant Card. Payload state is shared across
                        all three so counts on the triggers stay live.
                      */
                      <Tabs defaultValue="forbidden">
                        <TabsList className="mb-3">
                          <TabsTrigger value="forbidden">
                            Forbid edges ({inlinePayload.edges_forbidden.length})
                          </TabsTrigger>
                          <TabsTrigger value="required">
                            Require edges ({inlinePayload.edges_required.length})
                          </TabsTrigger>
                          <TabsTrigger value="tiers">
                            Tier ordering ({inlinePayload.tiers.length})
                          </TabsTrigger>
                        </TabsList>
                        <TabsContent value="forbidden" className="mt-0">
                          <KnowledgeEditor
                            columns={columns}
                            value={inlinePayload}
                            onChange={setInlinePayload}
                            disabled={isPending}
                            section="forbidden"
                          />
                        </TabsContent>
                        <TabsContent value="required" className="mt-0">
                          <KnowledgeEditor
                            columns={columns}
                            value={inlinePayload}
                            onChange={setInlinePayload}
                            disabled={isPending}
                            section="required"
                          />
                        </TabsContent>
                        <TabsContent value="tiers" className="mt-0">
                          <KnowledgeEditor
                            columns={columns}
                            value={inlinePayload}
                            onChange={setInlinePayload}
                            disabled={isPending}
                            section="tiers"
                          />
                        </TabsContent>
                      </Tabs>
                    )}
                    {!isPayloadEmpty(inlinePayload) ? (
                      <p className="mt-3 text-xs text-muted-foreground">
                        We'll save this as a named knowledge object the first time you click Run —
                        you can rename or delete it later from{' '}
                        <Link to="/knowledge" className="underline">
                          Knowledge
                        </Link>
                        .
                      </p>
                    ) : null}
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">3. Parameters</CardTitle>
              <CardDescription>Re-run with the same seed for reproducible output.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={form.control}
                name="seed"
                render={({ field }) => (
                  <FormItem className="max-w-xs">
                    <FormLabel>Random seed</FormLabel>
                    <FormControl>
                      <Input type="number" min={0} step={1} disabled={isPending} {...field} />
                    </FormControl>
                    <FormDescription>Default: 42</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/discoveries')}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isPending || !form.formState.isValid}>
              {isPending ? 'Starting…' : 'Run discovery'}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
